import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MovieCard from "@/components/MovieCard";
import MovieDetailDialog from "@/components/MovieDetailDialog";
import { Search, Film, CheckCircle2, Bookmark, Tag as TagIcon, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { openAdd } = useOutletContext();
  const [movies, setMovies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ total: 0, watched: 0, backlog: 0, groups: 0, tags: 0 });
  const [q, setQ] = useState("");
  const [search, setSearch] = useSearchParams();
  const statusFilter = search.get("status") || "all";
  const groupFilter = search.get("group") || "";
  const tagFilter = search.get("tag") || "";
  // Track which filter the current `movies` list was fetched for, so rapid toggles
  // within the same view don't kick movies out of the visible set.
  const fetchedFor = useRef("");
  const [detailMovie, setDetailMovie] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (groupFilter) params.group_id = groupFilter;
    if (tagFilter) params.tag = tagFilter;
    if (q.trim()) params.q = q.trim();
    const [m, g, s] = await Promise.all([
      api.get("/movies", { params }),
      api.get("/groups"),
      api.get("/stats"),
    ]);
    setMovies(m.data);
    setGroups(g.data);
    setStats(s.data);
    fetchedFor.current = `${statusFilter}|${groupFilter}|${tagFilter}|${q.trim()}`;
  }, [statusFilter, groupFilter, tagFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("movies:refresh", h);
    return () => window.removeEventListener("movies:refresh", h);
  }, [load]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(search);
    if (val) next.set(key, val); else next.delete(key);
    setSearch(next);
  };

  // Optimistic local update — don't refetch, just update the card in place.
  // This keeps order stable when the user rapid-toggles many cards.
  const handleLocalUpdate = useCallback((updated) => {
    setMovies(prev => prev.map(m => (m.id === updated.id ? updated : m)));
    setStats(prev => {
      const before = prev.watched;
      const list = movies.map(m => (m.id === updated.id ? updated : m));
      const watched = list.filter(m => m.status === "watched").length;
      if (watched === before) return prev;
      return { ...prev, watched, backlog: prev.total - watched };
    });
  }, [movies]);

  const handleLocalDelete = useCallback((id) => {
    setMovies(prev => prev.filter(m => m.id !== id));
    setStats(prev => {
      const removed = movies.find(m => m.id === id);
      if (!removed) return prev;
      const total = Math.max(0, prev.total - 1);
      const watched = removed.status === "watched" ? Math.max(0, prev.watched - 1) : prev.watched;
      return { ...prev, total, watched, backlog: total - watched };
    });
  }, [movies]);

  const statCards = [
    { label: "Total", value: stats.total, icon: Film, onClick: () => setFilter("status", "") },
    { label: "Backlog", value: stats.backlog, icon: Bookmark, onClick: () => setFilter("status", "backlog") },
    { label: "Watched", value: stats.watched, icon: CheckCircle2, onClick: () => setFilter("status", "watched") },
    { label: "Groups", value: stats.groups, icon: Sparkles },
    { label: "Tags", value: stats.tags, icon: TagIcon },
  ];

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E11D48] font-bold mb-2">Your library</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Movies to chase<span className="text-[#E11D48]">.</span></h1>
        </div>
        <div className="relative md:w-80">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search your library"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="library-search-input"
            className="pl-9 bg-[#0F0F12] border-white/10 focus-visible:ring-[#E11D48] text-white"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {statCards.map((s, i) => (
          <button
            key={i}
            onClick={s.onClick}
            disabled={!s.onClick}
            data-testid={`stat-${s.label.toLowerCase()}`}
            className={`p-5 rounded-xl bg-[#0F0F12] border border-white/5 text-left transition-all ${s.onClick ? "hover:border-[#E11D48]/40 hover:-translate-y-0.5" : ""}`}
          >
            <s.icon className="w-4 h-4 text-slate-500 mb-3" />
            <div className="font-display text-3xl font-bold">{s.value}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 bg-[#0F0F12] p-1 rounded-full border border-white/5" data-testid="status-tabs">
          {["all", "backlog", "watched"].map(s => (
            <button
              key={s}
              onClick={() => setFilter("status", s === "all" ? "" : s)}
              data-testid={`status-${s}`}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider transition-all ${
                (statusFilter === s || (s === "all" && !search.get("status"))) ? "bg-[#E11D48] text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-1.5 ml-2">
            <button
              onClick={() => setFilter("group", "")}
              data-testid="group-filter-all"
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${!groupFilter ? "bg-white/10 border-white/20 text-white" : "border-white/5 text-slate-400 hover:text-white"}`}
            >
              All groups
            </button>
            {groups.map(g => (
              <button
                key={g.id}
                onClick={() => setFilter("group", groupFilter === g.id ? "" : g.id)}
                data-testid={`group-filter-${g.id}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                  groupFilter === g.id ? "text-white" : "text-slate-300 hover:text-white border-white/5"
                }`}
                style={groupFilter === g.id ? { background: g.color, borderColor: g.color } : {}}
              >
                {g.name}
              </button>
            ))}
          </div>
        )}
        {tagFilter && (
          <button onClick={() => setFilter("tag", "")} className="text-xs text-slate-400 underline">Clear tag: {tagFilter}</button>
        )}
      </div>

      {/* Grid */}
      {movies.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl" data-testid="empty-state">
          <img
            src="https://images.pexels.com/photos/12895138/pexels-photo-12895138.jpeg?auto=compress&cs=tinysrgb&w=800"
            alt="projector"
            className="w-24 h-24 object-cover rounded-full mx-auto mb-6 opacity-60"
          />
          <p className="font-display text-xl mb-2">Your reel is empty</p>
          <p className="text-slate-400 text-sm mb-6">Add a movie to start building your backlog.</p>
          <Button onClick={openAdd} data-testid="empty-add-btn" className="bg-[#E11D48] hover:bg-rose-700 rounded-full px-6">
            Add your first movie
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5" data-testid="movies-grid">
          {movies.map(m => (
            <MovieCard
              key={m.id}
              movie={m}
              groups={groups}
              onLocalUpdate={handleLocalUpdate}
              onLocalDelete={handleLocalDelete}
              onOpen={(mv) => setDetailMovie(mv)}
            />
          ))}
        </div>
      )}

      <MovieDetailDialog
        movie={detailMovie}
        groups={groups}
        open={!!detailMovie}
        onOpenChange={(o) => !o && setDetailMovie(null)}
        onChange={(updated) => handleLocalUpdate(updated)}
        onDelete={(id) => handleLocalDelete(id)}
      />
    </div>
  );
}
